import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '../lib/api';

interface MonthCost { spentCents: number; capCents: number | null; }

/**
 * Top-bar AI usage chip (spec §6.3). Shows the month-to-date AI provider
 * passthrough cost + the workspace cap when set. The point is
 * transparency — Bakuraku hides this, we put it in the header.
 */
export function AiCostMeter({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery<MonthCost>({
    queryKey: ['ai-cost-month', workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/agents/cost/month`)).data,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const dollars = data ? (data.spentCents / 100).toFixed(2) : '—';
  const capDollars = data?.capCents != null ? (data.capCents / 100).toFixed(0) : null;
  const near = data && data.capCents != null && data.spentCents / data.capCents > 0.8;

  return (
    <div
      className={`hidden md:flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs num
        ${near
          ? 'border-warn-300 bg-warn-50 text-warn-700 dark:bg-warn-900/30'
          : 'border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-900 text-ink-600 dark:text-ink-300'
        }`}
      title={t('topbar.aiMeterHint')}
      aria-label={t('topbar.aiMeterAriaLabel', { dollars })}
    >
      <Sparkles size={12} className="text-accent-600" />
      <span>${dollars}</span>
      {capDollars && <span className="text-ink-400">/ ${capDollars}</span>}
    </div>
  );
}
